import { useState } from "react";
import {
    Typography,
    Box,
    Modal,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from "@mui/material";
import { requestData } from "../../remote/reportDataRequest";
import theme from "../../components/LayoutComponents/theme";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LoadingCircle from "../../components/LayoutComponents/LoadingCircle";
import PurpleButton from "../../components/LayoutComponents/PurpleButton";
import SkillProfileCard from "./SkillProfileCard";
import CourseCard from "./CourseCard";
import UserCard from "./UserCard";

const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "95%", sm: "80%" }, // smaller width on mobile
    maxHeight: "80vh",
    overflowY: "auto",
    bgcolor: "background.paper",
    borderRadius: "12px",
    boxShadow: 24,
    p: { xs: 2, sm: 4 },
};

export default function CompetencyReportingPage({ session, competencies = [] }) {
    const [reports, setReports] = useState({});
    const [loadingId, setLoadingId] = useState(null);
    const [open, setOpen] = useState(false);
    const [selectedCompetency, setSelectedCompetency] = useState(null);

    const fetchReportAndOpenModal = async (competency) => {
        const id = competency.compId;

        if (reports[id]) {
            setSelectedCompetency(competency);
            setOpen(true);
            return;
        }

        setLoadingId(id);
        try {
            const report = await requestData(session?.token, id);
            setReports((prev) => ({ ...prev, [id]: report.data }));
            setSelectedCompetency(competency);
            setOpen(true);
        } catch (err) {
            console.error("Error fetching competency report:", err);
        } finally {
            setLoadingId(null);
        }
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedCompetency(null);
    };

    const getSummaryStats = (report) => ({
        users: report?.users?.length || 0,
        skillProfiles: report?.skillProfiles?.length || 0,
        courses: report?.courses?.length || 0,
    });

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', mb: 3 }}>
                <Typography variant="h4" color={theme.colors.purple} sx={{ fontWeight: theme.typography.fontWeightBold }}>
                    Competency Report
                </Typography>
            </Box>

            {/* Responsive Table / Card rendering */}
{competencies.length > 0 ? (
  <Box sx={{ mt: 2 }}>
    {/* Large screens: Table */}
    <Box sx={{ display: { xs: "none", md: "block" }, overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 600 }}>
        <TableHead>
          <TableRow>
            <TableCell>Competency</TableCell>
            <TableCell>Users</TableCell>
            <TableCell>Skill Profiles</TableCell>
            <TableCell>Courses</TableCell>
            <TableCell>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {competencies.map((competency) => {
            const report = reports[competency.compId];
            const isLoading = loadingId === competency.compId;
            const stats = getSummaryStats(report);

            return (
              <TableRow key={competency.compId}>
                <TableCell>{competency.compDesc}</TableCell>
                <TableCell>{isLoading ? "-" : stats.users}</TableCell>
                <TableCell>{isLoading ? "-" : stats.skillProfiles}</TableCell>
                <TableCell>{isLoading ? "-" : stats.courses}</TableCell>
                <TableCell>
                  {isLoading ? (
                    <LoadingCircle size={30} />
                  ) : (
                    <Button
                      onClick={() => fetchReportAndOpenModal(competency)}
                      variant="outlined"
                      size="small"
                      sx={{ color: theme.colors.purple }}
                    >
                      {reports[competency.compId] ? "View Report" : "Generate Report"}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>

    {/* Small screens: Card layout */}
    <Box sx={{ display: { xs: "block", md: "none" }, gap: 2 }}>
      {competencies.map((competency) => {
        const report = reports[competency.compId];
        const isLoading = loadingId === competency.compId;
        const stats = getSummaryStats(report);

        return (
          <Box
            key={competency.compId}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              p: 2,
              mb: 2,
              backgroundColor: "white",
              boxShadow: 1,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
              {competency.compDesc}
            </Typography>
            <Typography variant="body2">Users: {isLoading ? "-" : stats.users}</Typography>
            <Typography variant="body2">
              Skill Profiles: {isLoading ? "-" : stats.skillProfiles}
            </Typography>
            <Typography variant="body2">Courses: {isLoading ? "-" : stats.courses}</Typography>
            <Box sx={{ mt: 1 }}>
              {isLoading ? (
                <LoadingCircle size={30} />
              ) : (
                <Button
                  onClick={() => fetchReportAndOpenModal(competency)}
                  variant="outlined"
                  size="small"
                  sx={{ color: theme.colors.purple }}
                >
                  {reports[competency.compId] ? "View Report" : "Generate Report"}
                </Button>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  </Box>
) : (
  <Typography>No competencies available.</Typography>
)}


            {/* Modal */}
            <Modal open={open} onClose={handleClose}>
                <Box sx={modalStyle}>
                    {selectedCompetency && reports[selectedCompetency.compId] ? (
                        <>
                            <Typography variant="h4" gutterBottom>
                                Report on Competency
                            </Typography>
                            <Typography variant="h6" gutterBottom>
                                {selectedCompetency.compDesc}
                            </Typography>

                            {/* Users Accordion */}
                            <Accordion sx={{ mt: 2 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h6">Users ({reports[selectedCompetency.compId].users?.length || 0})</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {reports[selectedCompetency.compId].users
                                        ?.sort((a, b) => a.lastName.localeCompare(b.lastName))
                                        .map((user) => (
                                            <UserCard key={user.userId} user={user} />
                                        )) || <Typography>No users found.</Typography>}
                                </AccordionDetails>
                            </Accordion>

                            {/* Skill Profiles Accordion */}
                            <Accordion sx={{ mt: 2 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h6">Skill Profiles ({reports[selectedCompetency.compId].skillProfiles?.length || 0})</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {reports[selectedCompetency.compId].skillProfiles
                                        ?.sort((a, b) => a.profileName.localeCompare(b.profileName))
                                        .map((profile) => (
                                            <SkillProfileCard key={profile.profileId} profile={profile} />
                                        )) || <Typography>No skill profiles found.</Typography>}
                                </AccordionDetails>
                            </Accordion>

                            {/* Courses Accordion */}
                            <Accordion sx={{ mt: 2 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h6">Courses ({reports[selectedCompetency.compId].courses?.length || 0})</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {reports[selectedCompetency.compId].courses
                                        .sort((a, b) => a.courseName.localeCompare(b.courseName))
                                        ?.map((course) => (
                                            <CourseCard key={course.courseId} course={course} />
                                        )) || <Typography>No courses found.</Typography>}
                                </AccordionDetails>
                            </Accordion>

                            <Box sx={{ textAlign: "right", mt: 2 }}>
                                <PurpleButton onClick={handleClose} label="Close" size="small" />
                            </Box>
                        </>
                    ) : (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                            <LoadingCircle />
                        </Box>
                    )}
                </Box>
            </Modal>
        </Box>
    );
}
